import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, User, Megaphone, Calendar } from 'lucide-react';
import { trackCompleteRegistration } from '../lib/metaPixel';
import Seo from '../components/Seo';

const ROLES = [
  {
    value: 'user',
    label: 'GO OUT',
    sub: 'I want to attend',
    icon: User,
    blurb: 'Save your favourite spots, RSVP to events, track your tickets.',
  },
  {
    value: 'promoter',
    label: 'PROMOTE',
    sub: 'I run guest lists',
    icon: Megaphone,
    blurb: 'Join events by code, build your own guest lists, scan guests at the door.',
  },
  {
    value: 'organizer',
    label: 'ORGANIZE',
    sub: 'I throw the events',
    icon: Calendar,
    blurb: 'Create events, hand out codes to promoters, see every guest list in one place.',
  },
];

const SignupPage = () => {
  const [searchParams] = useSearchParams();
  // Skip the role picker + jump straight to step 2 when the user is
  // coming from a post-RSVP or post-ticket "create account" nudge.
  const cameFromConversion = ['rsvp', 'ticket'].includes(searchParams.get('from') || '');

  const [step, setStep] = useState(cameFromConversion ? 2 : 1);
  const [role, setRole] = useState('user');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { signup } = useAuth();
  const navigate = useNavigate();

  // Prefill from ?email= and ?name= (post-conversion signup nudge)
  useEffect(() => {
    const prefEmail = searchParams.get('email');
    const prefName = searchParams.get('name');
    if (prefEmail) setEmail(prefEmail);
    if (prefName) setName(prefName);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const user = await signup(email, password, name, role);
      toast.success('Account created');
      // Meta CompleteRegistration — signals a new account creation to Meta
      // for Lookalike audiences + campaign optimization. `registration_method`
      // slices by role (user vs promoter vs organizer) and `from` slices by
      // the pre-fill funnel (rsvp / ticket) so the pixel captures campaign attribution.
      trackCompleteRegistration({
        method: role,
        contentName: searchParams.get('from') || 'direct',
      });
      // Drop them straight into the dashboard their role unlocks
      if (user.is_organizer) navigate('/organizer');
      else if (user.is_promoter) navigate('/promoter');
      else navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#050505] flex items-center justify-center px-4 pt-20 pb-12"
      data-testid="signup-page"
    >
      <Seo title="Create Account" description="Create a Unight account to save events, RSVP faster, and keep every ticket in one place." path="/signup" noindex />
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-[#0A0A0A] to-[#050505]" />
        <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-[#B5FF00]/5 blur-[100px] rounded-full" />
      </div>

      <div className="relative w-full max-w-2xl">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <span className="font-['Josefin_Sans'] text-3xl font-bold text-[#B5FF00]">UNIGHT</span>
          </Link>
          <h1 className="font-['Josefin_Sans'] text-4xl md:text-5xl font-bold text-white mb-2">
            CREATE ACCOUNT
          </h1>
          <p className="text-zinc-400">Join the underground</p>
        </div>

        {/* Step 1 — pick your role */}
        {step === 1 && (
          <div data-testid="signup-step-role">
            <p className="font-mono text-xs text-zinc-500 tracking-widest mb-4 text-center">
              STEP 1 OF 2 · WHY ARE YOU HERE?
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {ROLES.map((r) => {
                const Icon = r.icon;
                const active = role === r.value;
                return (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setRole(r.value)}
                    className={`text-left p-5 border transition-all ${
                      active
                        ? 'border-[#B5FF00] bg-[#B5FF00]/5'
                        : 'border-zinc-800 bg-[#0A0A0A] hover:border-zinc-600'
                    }`}
                    data-testid={`signup-role-${r.value}`}
                  >
                    <Icon size={28} className={active ? 'text-[#B5FF00] mb-3' : 'text-zinc-500 mb-3'} />
                    <h3 className="font-['Josefin_Sans'] text-xl font-bold text-white mb-1">{r.label}</h3>
                    <p className="font-mono text-xs text-zinc-500 tracking-wider mb-3">{r.sub}</p>
                    <p className="text-sm text-zinc-400 leading-relaxed">{r.blurb}</p>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setStep(2)}
              className="btn-primary w-full"
              data-testid="signup-next-btn"
            >
              CONTINUE
            </button>
            <p className="text-center mt-8 text-zinc-500 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-[#B5FF00] hover:underline">Sign in</Link>
            </p>
          </div>
        )}

        {/* Step 2 — credentials */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-6 max-w-md mx-auto">
            <p className="font-mono text-xs text-zinc-500 tracking-widest mb-2 text-center">
              STEP 2 OF 2 · YOUR DETAILS
            </p>
            <div className="text-center mb-4">
              <span className="inline-block font-mono text-xs tracking-widest text-[#B5FF00] border border-[#B5FF00]/40 bg-[#B5FF00]/5 px-3 py-1.5">
                {ROLES.find((r) => r.value === role)?.label}
              </span>
              {' '}
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-zinc-500 hover:text-white font-mono text-xs tracking-widest underline ml-2"
                data-testid="signup-change-role-btn"
              >
                CHANGE
              </button>
            </div>

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">NAME</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full h-12 bg-[#0A0A0A] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                placeholder="Your name"
                required
                data-testid="signup-name"
              />
            </div>

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">EMAIL</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-12 bg-[#0A0A0A] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                placeholder="your@email.com"
                required
                data-testid="signup-email"
              />
            </div>

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">PASSWORD</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 bg-[#0A0A0A] border border-zinc-800 text-white px-4 pr-12 focus:border-[#B5FF00] focus:outline-none"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="signup-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-zinc-600 text-xs mt-2 font-mono">Minimum 6 characters</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="signup-submit"
            >
              {loading ? (<><Loader2 size={18} className="animate-spin" /> CREATING...</>) : 'CREATE ACCOUNT'}
            </button>

            <p className="text-center text-zinc-500 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-[#B5FF00] hover:underline" data-testid="login-link">Sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default SignupPage;
